import React, { useState } from "react";

import { performUpdateUserReloadCarrier } from "../../../../graphql/carrier/carrierCRUD";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";

import { Button, useNotify, useRefresh } from "react-admin";

import { CancelIcon, TransferIcon } from "../../../../components/common/iconography";
import { logError } from "../../../../utils/appUtils";
import { fetchCarrierList } from "../../../../utils/carrierListCache";

/**
 * The membership this button would move, if there is one to move.
 *
 * Deliberately the same row the rest of the screen is about — the aside names
 * its carrier, the form saves its notes and block-access setting — rather than
 * the first active row found. Moving a different row than the one on display
 * would leave the manager's edits attached to a company they can no longer see.
 *
 * Drivers only. A dispatch user's carrier is chosen on the invitation form, and
 * the existing way to correct it is to delete and re-invite: nothing is lost
 * doing that (they have no gate photo, no ID document, no check-in history at
 * the wrong company), while moving one raises questions this button does not
 * answer — a carrier's primary account moved to another company would leave the
 * first with no dispatch login and hand its own address to the second.
 *
 * @param {object} record - a CarrierUser with its reloadYardCarriers
 * @returns {object|null} the junction row, or null when there is nothing to move
 */
export const movableMembership = (record) => {
  if (record?.userType !== "driver") return null;
  const membership = record?.reloadYardCarriers?.items?.[0];
  if (!membership || membership.active !== "true") return null;
  // Another yard's row is not this manager's to move. Junctions are filtered by
  // yard group before they reach here, so this should never fire — but a move
  // writing another yard's membership is worth one comparison to rule out.
  if (membership.reloadYardId !== window.appUser?.reloadYardId) return null;
  return membership;
};

/**
 * What moving this driver to `carrier` would write, or why it cannot be.
 *
 * The junction is rewritten in place rather than retired and re-created. One
 * write means the driver is never briefly without a membership — a half-done
 * move would leave them unable to check in at the gate — and everything hanging
 * off the row (gate photo, attachments, notes, block-access, permissions, the
 * driver's own enrollment) stays attached without being copied.
 *
 * All four members of the reloadYardCarrierByActiveByTypeByName sort key travel
 * with the spread: businessName changes, and the resolver composes the key from
 * the input alone, so a partial input would drop the row out of the manager's
 * Drivers tab entirely (see sanitizeUserReloadCarrier).
 *
 * @param {object} record - a CarrierUser with its reloadYardCarriers
 * @param {object} carrier - the carrier to move to
 * @returns {{input: object}|{error: string}}
 */
export const planCarrierMove = (record, carrier) => {
  const membership = movableMembership(record);
  if (!membership) {
    return { error: "This driver has no active membership at your transload." };
  }
  if (!carrier?.id) return { error: "Choose the carrier to move this driver to." };
  if (carrier.id === membership.carrierId) {
    return { error: `Already on file with ${membership.businessName}.` };
  }
  // A second active row for the same person at the same carrier is the one
  // state the gate cannot read honestly: getMembership has to pick one, and the
  // manager's Drivers tab would list the driver twice under one company.
  const clash = (record?.reloadYardCarriers?.items ?? []).find(
    (row) =>
      row &&
      row.id !== membership.id &&
      row.active === "true" &&
      row.reloadYardId === membership.reloadYardId &&
      row.carrierId === carrier.id,
  );
  if (clash) {
    return {
      error: `This driver already works with ${carrier.businessName} at your transload.`,
    };
  }
  return {
    input: {
      ...membership,
      carrierId: carrier.id,
      businessName: carrier.businessName,
    },
  };
};

/**
 * Put a driver on file with a different carrier.
 *
 * A driver enrolls themselves at the gate and names their own company, so the
 * company on their record can be wrong in ways no one at the yard chose: a
 * name typed into a new carrier entry that duplicates an existing one, or a
 * driver let go by one company and hired by the next who confirmed the change
 * on nobody's kiosk. Until this button the only correction was to revoke the
 * driver and have them enroll again, which costs them their gate photo and
 * their ID document and costs the yard the link to their check-in history.
 *
 * The move takes effect at the gate immediately: a driver session reads its
 * membership fresh on every request rather than caching it, so the next
 * check-in is credited to the new company. Check-ins already recorded keep the
 * carrier they were made under — those trips really were run for it.
 *
 * Writing the junction straight away means unsaved notes or a just-changed
 * block-access setting are left behind on a form whose record has moved on.
 * This used to be enforced by disabling the button while the form was dirty,
 * which is not available from the top toolbar — react-admin renders actions
 * outside the form, so there is no form state to read. The dialog says so
 * instead.
 */
export const MoveCarrierButton = ({ record, className, ...props }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [carriers, setCarriers] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [target, setTarget] = useState(null);
  const [moving, setMoving] = useState(false);
  const [error, setError] = useState(null);

  const membership = movableMembership(record);

  const handleOpen = () => {
    setTarget(null);
    setError(null);
    setOpen(true);
    setFetching(true);
    // The shared list the carrier dropdowns use: active carriers at this yard,
    // including any created during this session that the GSI has not caught up
    // with. The current carrier is dropped — moving a driver to the company
    // they are already on file with is the one choice that means nothing.
    fetchCarrierList(window.appUser?.reloadYardId)
      .then((items) =>
        setCarriers(
          (items ?? []).filter((c) => c && c.id !== membership.carrierId),
        ),
      )
      .catch((err) =>
        logError(
          "managerApp:carrierUsers:MoveCarrierButton:fetchCarrierList",
          err,
          notify,
        ),
      )
      .finally(() => setFetching(false));
  };

  const handleClose = () => {
    if (moving) return;
    setOpen(false);
  };

  const handleMove = async () => {
    const plan = planCarrierMove(record, target);
    if (plan.error) {
      setError(plan.error);
      return;
    }
    setMoving(true);
    try {
      await performUpdateUserReloadCarrier(plan.input);
      notify(`Driver moved to ${target.businessName}.`, "success");
      setOpen(false);
      // Refreshed rather than redirected: the manager asked a question about
      // this driver, and the answer is the carrier the aside now names.
      refresh();
    } catch (err) {
      logError("managerApp:carrierUsers:MoveCarrierButton:handleMove", err, notify);
    } finally {
      setMoving(false);
    }
  };

  if (!membership) return null;

  const driverName = [record.firstName, record.lastName].filter(Boolean).join(" ");

  return (
    <>
      <Button
        {...props}
        label="Move"
        color="default"
        disabled={moving}
        onClick={handleOpen}
        className={className}
      >
        <TransferIcon />
      </Button>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Move to another carrier</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {driverName || "This driver"} is on file with{" "}
            <strong>{membership.businessName}</strong>.
          </DialogContentText>
          <Autocomplete
            options={carriers}
            value={target}
            loading={fetching}
            getOptionLabel={(option) => option?.businessName ?? ""}
            getOptionSelected={(option, value) => option?.id === value?.id}
            onChange={(_, selected) => {
              setTarget(selected || null);
              setError(null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Move to"
                variant="outlined"
                fullWidth
              />
            )}
          />
          <DialogContentText style={{ marginTop: 16 }}>
            Their driver ID, gate photo, documents, notes and access at your
            transload move with them, and their next check-in is credited to the
            new carrier. Check-ins already recorded keep the carrier they were
            made under.
          </DialogContentText>
          <DialogContentText>
            Any unsaved changes on the form behind this dialog are discarded —
            save them first if you want to keep them.
          </DialogContentText>
          {error && <Typography color="error">{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button
            label="Cancel"
            color="default"
            disabled={moving}
            onClick={handleClose}
          >
            <CancelIcon />
          </Button>
          <Button
            label="Move"
            variant="contained"
            disabled={moving || !target}
            onClick={handleMove}
          >
            <TransferIcon />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
